/**
 * 캐릭터 SVG 스프라이트 로더
 *
 * SVG 파일 위치: `web-client/public/assets/gfx/chars/{char}_{weapon}_{direction}_{frame}.svg`
 *
 * 총 14캐릭터 × 4무기 × 8방향 × 5프레임 = 2,240개 파일이므로 부팅 시 모두
 * 로드하면 첫 화면이 늦게 뜬다. 그래서 **지연 로딩** 방식을 쓴다:
 *   - GameScene에서 엔티티를 처음 화면에 그릴 때 loadSprite()를 호출
 *   - 내부 캐시(Map)에 HTMLImageElement를 보관하고, 같은 키 재요청 시 즉시 반환
 *   - 로드 실패는 호출자에게 Promise rejection으로 전파 — 씬에서 fallback 텍스트를
 *     보여줄 수 있도록 그대로 둔다.
 *
 * Phaser의 텍스처 시스템과 통합하기 위해, loadSvgAsTexture()가 SVG를 fetch →
 * base64 data URL로 인코딩 → scene.textures.addBase64()로 등록한다. 이후에는
 * Phaser 키(이름)만 알면 즉시 this.add.image(key)로 쓸 수 있다.
 */

export type CharacterId =
  | 'prince_m' | 'princess_f'
  | 'knight_m' | 'knight_f'
  | 'elf_m' | 'elf_f'
  | 'wizard_m' | 'wizard_f'
  | 'darkelf_m' | 'darkelf_f'
  | 'dragonknight_m' | 'dragonknight_f'
  | 'illusionist_m' | 'illusionist_f';

export type WeaponType = 'none' | 'sword' | 'bow' | 'staff';

export type Direction = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

export const CHARACTER_IDS: CharacterId[] = [
  'prince_m', 'princess_f',
  'knight_m', 'knight_f',
  'elf_m', 'elf_f',
  'wizard_m', 'wizard_f',
  'darkelf_m', 'darkelf_f',
  'dragonknight_m', 'dragonknight_f',
  'illusionist_m', 'illusionist_f',
];

export const WEAPONS: WeaponType[] = ['none', 'sword', 'bow', 'staff'];
export const DIRECTIONS: Direction[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** 프레임 수 (0 = idle, 1~4 = 걷기). */
export const FRAME_COUNT = 5;
/** 서 있는 기본 프레임. */
export const IDLE_FRAME = 0;

/**
 * 사망 애니메이션 프레임 종류.
 *
 *  - 0: 쓰러짐 직전 (정상 자세, 살짝 흔들림)
 *  - 1: 낙하 중 (45° 기울기)
 *  - 2: 임팩트 (90° 회전 + 손발 펼쳐짐)
 *  - 3: 완전 누움 (지면에 붙은 상태)
 *  - 4: 묘비 († 십자 표시) — 애니메이션 종료 후 고정 상태
 */
export type DeathFrame = 0 | 1 | 2 | 3 | 4;
export const DEATH_FRAME_COUNT = 5;
/** 묘비로 고정되는 마지막 프레임. */
export const DEATH_FINAL_FRAME: DeathFrame = 4;

/**
 * 사망 애니메이션의 프레임 전환 주기(ms).
 * 5프레임 × 200ms = 1000ms = 1초 사망 애니메이션.
 * GameScene의 update()가 이 값을 기준으로 frame을 advance한다.
 */
export const DEATH_FRAME_DURATION_MS = 200;

/**
 * 서버 heading(0~7) → 8방향 매핑.
 * 서버의 HEADING_TABLE과 일치한다 (isometric.ts HEADING_VECTORS와 동일한 순서).
 */
export function headingToDirection(heading: number): Direction {
  const idx = ((Math.floor(heading) % 8) + 8) % 8;
  return DIRECTIONS[idx];
}

/**
 * 캐릭터 SVG의 URL 경로를 만든다.
 */
export function getCharSpritePath(
  char: CharacterId,
  weapon: WeaponType,
  dir: Direction,
  frame: number,
): string {
  const safeFrame = Math.max(0, Math.min(FRAME_COUNT - 1, Math.floor(frame)));
  return `/assets/gfx/chars/${char}_${weapon}_${dir}_${safeFrame}.svg`;
}

/**
 * 캐릭터 스프라이트의 Phaser 텍스처 키.
 * 같은 캐릭터/무기/방향/프레임 조합이면 항상 같은 키를 반환한다.
 */
export function getCharSpriteKey(
  char: CharacterId,
  weapon: WeaponType,
  dir: Direction,
  frame: number,
): string {
  return `char|${char}|${weapon}|${dir}|${frame}`;
}

/**
 * 클래스 인덱스(0~6) → [남성, 여성] CharacterId.
 * 코드 체계는 CharSelectScene의 CLASS_NAMES와 같다
 * (0=군주, 1=기사, 2=요정, 3=마법사, 4=다크엘프, 5=용기사, 6=환술사).
 */
const CLASS_CHARACTERS: Array<[CharacterId, CharacterId]> = [
  ['prince_m', 'princess_f'],
  ['knight_m', 'knight_f'],
  ['elf_m', 'elf_f'],
  ['wizard_m', 'wizard_f'],
  ['darkelf_m', 'darkelf_f'],
  ['dragonknight_m', 'dragonknight_f'],
  ['illusionist_m', 'illusionist_f'],
];

/**
 * 클래스 인덱스(0~6) → [남성 gfxId, 여성 gfxId].
 *
 * **서버 `C_CreateChar`의 MALE_LIST / FEMALE_LIST와 반드시 같아야 한다.**
 *   MALE_LIST   = { 0, 61, 138, 734, 2786, 6658, 6671 }
 *   FEMALE_LIST = { 1, 48, 37, 1186, 2796, 6661, 6650 }
 * 이 값이 characters.class 컬럼에 저장되고 S_OwnCharPack의 gfxId로 전송된다.
 */
const CLASS_GFX_IDS: Array<[number, number]> = [
  [0, 1],       // 군주 / 공주
  [61, 48],     // 기사
  [138, 37],    // 요정
  [734, 1186],  // 마법사
  [2786, 2796], // 다크엘프
  [6658, 6661], // 용기사
  [6671, 6650], // 환술사
];

/**
 * gfxId → CharacterId.
 *
 * 위 두 표에서 파생시킨다 — 손으로 따로 적으면 클래스 판정 경로(gfxId 기반)와
 * 프리로드 경로(클래스 기반)가 서로 다른 캐릭터를 가리키게 되고, 미리 받아둔
 * 텍스처가 통째로 쓰이지 않는다.
 */
const GFX_TO_CHARACTER: Record<number, CharacterId> = (() => {
  const map: Record<number, CharacterId> = {};
  CLASS_GFX_IDS.forEach(([maleGfx, femaleGfx], classIndex) => {
    const [male, female] = CLASS_CHARACTERS[classIndex];
    map[maleGfx] = male;
    map[femaleGfx] = female;
  });
  return map;
})();

/** 매핑되지 않은 gfxId는 prince_m으로 폴백한다 (NPC/몬스터 등). */
export function inferCharacter(gfxId: number, _isMale?: boolean): CharacterId {
  return GFX_TO_CHARACTER[gfxId] ?? 'prince_m';
}

/**
 * 클래스/성별로 CharacterId를 구한다. (sex: 0=남, 1=여)
 *
 * 캐릭터 목록 패킷에는 gfxId가 없어 inferCharacter를 쓸 수 없다. 대신 클래스와
 * 성별이 오므로 이 매핑으로 같은 CharacterId를 얻는다 — 캐릭터 선택 화면에서
 * 미리 받아둔 텍스처를 GameScene이 그대로 재사용하려면 양쪽 결과가 같아야 한다.
 */
export function characterIdFromClass(type: number, sex: number): CharacterId {
  const pair = CLASS_CHARACTERS[type];
  if (!pair) return 'prince_m';
  return sex === 0 ? pair[0] : pair[1];
}

/**
 * 무기 추론.
 * 현재는 인벤토리/장비 정보를 따로 받지 않으므로 항상 'none'으로 폴백한다.
 * 추후 장비 슬롯 데이터가 도착하면 그쪽에서 무기를 결정하도록 확장한다.
 */
export function inferWeapon(_gfxId: number, _type: 'player' | 'npc' | 'monster'): WeaponType {
  return 'none';
}

// ============================================================
// Phaser 텍스처 통합 (SVG → base64 → textures.addBase64)
// ============================================================

/** 한 번 로드된 키는 다시 fetch하지 않기 위한 캐시. */
const textureLoaded: Set<string> = new Set();

/** 동시에 같은 키가 요청되면 같은 Promise를 공유하도록 보류 중인 요청을 보관. */
const pendingTextures: Map<string, Promise<string>> = new Map();

/**
 * SVG 파일을 fetch → base64 data URL로 변환 → Phaser 텍스처로 등록한다.
 * 이미 등록된 키면 즉시 resolve한다.
 *
 * @returns 등록된 Phaser 텍스처 키 (입력으로 받은 key 그대로).
 */
export async function loadSvgAsTexture(
  scene: Phaser.Scene,
  key: string,
  url: string,
): Promise<string> {
  if (textureLoaded.has(key)) return key;
  if (scene.textures.exists(key)) {
    textureLoaded.add(key);
    return key;
  }

  const existing = pendingTextures.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${url} (${response.status})`);
    }
    const svgText = await response.text();
    // base64로 인코딩해 data URL로 만든다.
    const base64 = btoa(unescape(encodeURIComponent(svgText)));
    const dataUrl = `data:image/svg+xml;base64,${base64}`;

    // 이미지 디코딩이 끝난 뒤에 텍스처로 등록한다.
    //
    // textures.addBase64()를 쓰면 안 된다 — 그 함수는 내부적으로 Image를 만들어
    // onload에서 텍스처를 완성하는 **비동기** 동작이라, 호출 직후 resolve하면
    // 호출부가 아직 프레임이 없는 텍스처에 setTexture()를 걸게 되고 Phaser가
    // '__MISSING'으로 폴백한다. 그 뒤 텍스처가 완성돼도 스프라이트는 갱신되지 않는다.
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`SVG 디코딩 실패: ${url}`));
      image.src = dataUrl;
    });

    // 디코딩 대기 중 다른 호출이 먼저 등록했을 수 있다.
    if (!scene.textures.exists(key)) {
      scene.textures.addImage(key, image);
    }

    textureLoaded.add(key);
    return key;
  })().catch((err) => {
    pendingTextures.delete(key);
    throw err;
  });

  pendingTextures.set(key, promise);
  return promise;
}

/**
 * PNG 스프라이트시트(가로로 이어 붙인 프레임들)를 Phaser 텍스처로 등록한다.
 *
 * SVG 경로(loadSvgAsTexture)와 같은 캐시·중복요청 병합을 쓰되, 등록만
 * `addSpriteSheet`으로 한다 — 프레임을 나눠 두어야 `setTexture(key, index)`로
 * 프레임을 골라 쓸 수 있다.
 *
 * 실사 몹 그림이 이 경로를 쓴다(`utils/mobSprites.ts`). 원본이 애니메이션 GIF라
 * 생성기가 프레임을 뽑아 한 장으로 이어 붙여 두었다.
 *
 * @returns 등록된 Phaser 텍스처 키 (입력으로 받은 key 그대로).
 */
export async function loadSpriteSheetTexture(
  scene: Phaser.Scene,
  key: string,
  url: string,
  frameWidth: number,
  frameHeight: number,
): Promise<string> {
  if (textureLoaded.has(key)) return key;
  if (scene.textures.exists(key)) {
    textureLoaded.add(key);
    return key;
  }

  const existing = pendingTextures.get(key);
  if (existing) return existing;

  const promise = (async () => {
    // 디코딩이 끝난 뒤에 등록한다 — 이유는 loadSvgAsTexture의 주석과 같다.
    const image = new Image();
    image.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`스프라이트시트 로드 실패: ${url}`));
      image.src = url;
    });

    if (!scene.textures.exists(key)) {
      scene.textures.addSpriteSheet(key, image, { frameWidth, frameHeight });
    }

    textureLoaded.add(key);
    return key;
  })().catch((err) => {
    pendingTextures.delete(key);
    throw err;
  });

  pendingTextures.set(key, promise);
  return promise;
}

/**
 * 캐릭터 스프라이트의 SVG를 비동기로 로드하고 Phaser 텍스처로 등록한다.
 * @returns 등록된 텍스처 키.
 */
export function loadCharSprite(
  scene: Phaser.Scene,
  char: CharacterId,
  weapon: WeaponType,
  dir: Direction,
  frame: number,
): Promise<string> {
  const key = getCharSpriteKey(char, weapon, dir, frame);
  if (textureLoaded.has(key)) return Promise.resolve(key);
  const url = getCharSpritePath(char, weapon, dir, frame);
  return loadSvgAsTexture(scene, key, url);
}

/**
 * 한 캐릭터가 게임에서 쓰게 될 스프라이트(8방향 × 전 프레임)를 미리 받아둔다.
 *
 * 캐릭터 선택 화면처럼 네트워크가 한가한 시점에 호출하면, 월드 진입 직후
 * 지연 로딩 때문에 캐릭터가 한 박자 늦게 나타나는 것을 없앨 수 있다.
 *
 * 개별 실패는 삼킨다 — 프리로드는 최적화일 뿐이고, 못 받은 것은 GameScene이
 * 필요할 때 다시 요청한다. 호출자가 화면을 막지 않도록 await하지 않아도 된다.
 *
 * @returns 실제로 준비된 텍스처 수.
 */
export async function preloadCharacterSprites(
  scene: Phaser.Scene,
  char: CharacterId,
  weapon: WeaponType = 'none',
): Promise<number> {
  const tasks: Promise<string | null>[] = [];

  for (const dir of DIRECTIONS) {
    for (let frame = 0; frame < FRAME_COUNT; frame++) {
      tasks.push(loadCharSprite(scene, char, weapon, dir, frame).catch(() => null));
    }
  }

  const results = await Promise.all(tasks);
  return results.filter((key) => key !== null).length;
}

/** 디버깅/테스트용 캐시 상태. */
export function getSpriteCacheStats(): { registered: number } {
  return { registered: textureLoaded.size };
}

/** 테스트용 캐시 초기화. 실제 게임 흐름에서는 호출하지 말 것. */
export function clearSpriteCache(): void {
  textureLoaded.clear();
  pendingTextures.clear();
}

// ============================================================
// 사망 스프라이트 (캐릭터 + 8방향 + 5프레임)
// ============================================================
//
// 일반 캐릭터 스프라이트와 같은 loadSvgAsTexture() 파이프라인을 그대로 쓴다.
// Phaser 텍스처 키만 `death|{char}|{dir}|{frame}` 형태로 일반 스프라이트와
// 분리되므로 walking/idle 텍스처와 충돌하지 않는다.

/**
 * 사망 SVG의 URL 경로를 만든다. 파일은 다른 에이전트가 생성 중이며
 * `web-client/public/assets/gfx/death/{char}_{dir}_{frame}.svg` 위치에 있다.
 */
export function getDeathSpritePath(
  char: CharacterId,
  dir: Direction,
  frame: DeathFrame,
): string {
  const safeFrame = Math.max(0, Math.min(DEATH_FRAME_COUNT - 1, Math.floor(frame)));
  return `/assets/gfx/death/${char}_${dir}_${safeFrame}.svg`;
}

/**
 * 사망 스프라이트의 Phaser 텍스처 키.
 * 같은 (char, dir, frame) 조합이면 항상 같은 키를 반환한다.
 */
export function getDeathSpriteKey(
  char: CharacterId,
  dir: Direction,
  frame: DeathFrame,
): string {
  return `death|${char}|${dir}|${frame}`;
}

/**
 * 사망 스프라이트 SVG를 비동기로 로드하고 Phaser 텍스처로 등록한다.
 * @returns 등록된 텍스처 키.
 */
export function loadDeathSprite(
  scene: Phaser.Scene,
  char: CharacterId,
  dir: Direction,
  frame: DeathFrame,
): Promise<string> {
  const key = getDeathSpriteKey(char, dir, frame);
  if (textureLoaded.has(key)) return Promise.resolve(key);
  const url = getDeathSpritePath(char, dir, frame);
  return loadSvgAsTexture(scene, key, url);
}

/**
 * 한 캐릭터의 사망 스프라이트(8방향 × 5프레임 = 40장)를 미리 받아둔다.
 *
 * 캐릭터별 사망이 일어나기 직전에 호출하면 frame 사이의 텍스처 깜빡임을
 * 줄일 수 있다. 호출자가 화면을 막지 않도록 await하지 않아도 된다.
 *
 * 개별 실패는 삼킨다 — 못 받은 것은 GameScene이 필요할 때 다시 요청한다.
 *
 * @returns 실제로 준비된 텍스처 수.
 */
export async function preloadCharacterDeathSprites(
  scene: Phaser.Scene,
  char: CharacterId,
): Promise<number> {
  const tasks: Promise<string | null>[] = [];

  for (const dir of DIRECTIONS) {
    for (let frame = 0 as DeathFrame; frame < DEATH_FRAME_COUNT; frame = (frame + 1) as DeathFrame) {
      tasks.push(loadDeathSprite(scene, char, dir, frame).catch(() => null));
    }
  }

  const results = await Promise.all(tasks);
  return results.filter((key) => key !== null).length;
}

/**
 * 시작 시각으로부터 현재 death frame을 계산한다.
 *
 * @param startedAt  ms 단위 시작 시각 (예: `this.time.now`)
 * @param now        ms 단위 현재 시각
 * @returns          frame (0..4). 4(묘비) 이후에도 4를 반환한다.
 */
export function calcDeathFrame(startedAt: number, now: number): DeathFrame {
  const elapsed = Math.max(0, now - startedAt);
  const frame = Math.floor(elapsed / DEATH_FRAME_DURATION_MS);
  return Math.min(DEATH_FINAL_FRAME, Math.max(0, frame)) as DeathFrame;
}
